import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { scrollToId } from "@/hooks/useSmoothScroll";
import BrandLockup from "@/components/BrandLockup";

const links = [
  { label: "Problem", href: "#problem" },
  { label: "Leistungen", href: "#leistungen" },
  { label: "Ablauf", href: "#ablauf" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 2.0, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[#FAF8F2]/90 backdrop-blur-md border-b border-slate-200"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <button
            data-testid="logo-home"
            onClick={() => (window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0 }))}
            className="flex items-center"
            aria-label="Kurpfalz Automation – Startseite"
          >
            <BrandLockup compact />
          </button>

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-${l.href.replace("#", "")}`}
                onClick={() => go(l.href)}
                className="relative text-sm font-semibold text-[#16273F]/70 hover:text-[#16273F] transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#9A8420] after:transition-all hover:after:w-full"
              >
                {l.label}
              </button>
            ))}
            <Link
              to="/blog"
              data-testid="nav-blog"
              className="relative text-sm font-semibold text-[#16273F]/70 hover:text-[#16273F] transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#9A8420] after:transition-all hover:after:w-full"
            >
              Blog
            </Link>
            <button
              data-testid="nav-kontakt-cta"
              onClick={() => go("#kontakt")}
              className="inline-flex items-center h-11 px-6 rounded-full bg-[#16273F] hover:bg-[#0E1B2E] text-white text-sm font-semibold transition-colors"
            >
              Kontakt
            </button>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-full text-[#16273F] hover:bg-[#16273F]/5 transition-colors"
            aria-label="Menü öffnen"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            data-testid="mobile-menu"
            className="md:hidden overflow-hidden border-t border-slate-200 bg-[#FAF8F2]"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <button
                  key={l.href}
                  data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                  onClick={() => go(l.href)}
                  className="text-left py-3 px-2 rounded-lg text-base font-semibold text-[#16273F] hover:bg-[#16273F]/5 transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/blog"
                data-testid="mobile-nav-blog"
                onClick={() => setOpen(false)}
                className="text-left py-3 px-2 rounded-lg text-base font-semibold text-[#16273F] hover:bg-[#16273F]/5 transition-colors"
              >
                Blog
              </Link>
              <button
                data-testid="mobile-nav-kontakt-cta"
                onClick={() => go("#kontakt")}
                className="mt-2 inline-flex items-center justify-center h-12 px-5 rounded-full bg-[#16273F] text-white text-base font-semibold"
              >
                Kontakt aufnehmen
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
